import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import sortBy from 'Utils/sortBy';
import { navigationHelper } from 'NavigationHelpers';
import {
    getActiveEnrollmentAgreementSigningLink,
    getHasActiveEnrollmentAgreementSigningLink,
    hasSignedEnrollmentAgreement,
    getTilaDisclosureSigningLink,
    getHasTilaDisclosureSigningLink,
    getHasSignedTilaDisclosure,
    getRequiresTilaDisclosure,
    getActiveIdVerificationPeriod,
} from 'Users';
import { getFirstIncompleteForm } from 'FormCompletionHelper';
import { StudentProfileFormConfigs } from 'FormConfigs';

export default function getEnrollmentSidebarTodoGroups($injector) {
    const $rootScope = $injector.get('$rootScope');
    const user = $rootScope.currentUser;
    if (!user || !user.career_profile) {
        return [[], {}];
    }
    const { loadRoute, loadUrl, loadFaqUrl } = navigationHelper();

    const UserIdVerificationViewModel = $injector.get('UserIdVerificationViewModel');
    const offlineModeManager = $injector.get('offlineModeManager');

    const userIdVerificationViewModel = new UserIdVerificationViewModel();
    userIdVerificationViewModel.launchVerificationModalIfLastIdologyVerificationFailed();

    const handleClick = (enrollmentTodo, callback) => {
        if (enrollmentTodo.hasBeenCompleted() || enrollmentTodo.disabled()) {
            return;
        }
        callback();
    };

    const todoDisabled = () => offlineModeManager.inOfflineMode;

    /*
        NOTE: This list of requirements is essentially duplicated in
        program_inclusion.rb#determine_if_all_enrollment_requirements_met.
        If we make edits here, we may need to make edits there as well.
    */
    const todoItems = [
        {
            name: 'todo_enrollment_agreement',
            localeKey: 'sign_enrollment_agreement',
            isVisible: () => hasSignedEnrollmentAgreement(user) || getHasActiveEnrollmentAgreementSigningLink(user),
            hasBeenCompleted: () => hasSignedEnrollmentAgreement(user),
            onClick() {
                handleClick(this, () => loadUrl(getActiveEnrollmentAgreementSigningLink(user), '_blank'));
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDocumentsDeadline,
        },
        {
            name: 'todo_unofficial_transcripts',
            localeKey:
                user.career_profile.educationExperiencesThatCanRequireOfficial.length > 1
                    ? 'upload_unofficial_transcripts'
                    : 'upload_unofficial_transcript',
            isVisible: () => {
                if (!user.relevantCohort?.supportsDocumentUpload) return false;
                const isPastTranscriptRequirement = user.isCurrentOrHasCompletedProgram || user.transcripts_verified;
                const providedZeroUnofficialTranscripts =
                    user.career_profile.educationExperienceRequiringOfficialTranscript?.unofficialTranscripts
                        ?.length === 0;
                const officialTranscriptInReview =
                    user.career_profile.educationExperienceRequiringOfficialTranscript?.officialTranscriptInReview;
                return (
                    user.recordsIndicateUserShouldProvideTranscripts &&
                    !(isPastTranscriptRequirement && providedZeroUnofficialTranscripts) &&
                    !officialTranscriptInReview
                );
            },
            hasBeenCompleted: () =>
                user.career_profile.educationExperienceRequiringOfficialTranscript?.unofficial_transcripts_approved ||
                (user.transcripts_verified &&
                    user.career_profile.educationExperienceRequiringOfficialTranscript?.unofficialTranscripts
                        ?.length !== 0),
            inReview: () =>
                !user.transcripts_verified &&
                !user.career_profile.educationExperienceRequiringOfficialTranscript
                    ?.unofficial_transcripts_needs_more_information &&
                user.career_profile.educationExperienceRequiringOfficialTranscript?.unofficialTranscripts?.length !==
                    0 &&
                !user.career_profile.educationExperienceRequiringOfficialTranscript?.unofficial_transcripts_approved,
            needsMoreInfo: () =>
                !user.transcripts_verified &&
                !!user.career_profile.educationExperienceRequiringOfficialTranscript
                    ?.unofficial_transcripts_needs_more_information,
            onClick() {
                handleClick(this, () => loadRoute('/settings/documents'));
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDocumentsDeadline,
        },
        {
            name: 'todo_english_language',
            localeKey: 'upload_english_language_documents',
            isVisible: () => user.recordsIndicateUserShouldUploadEnglishLanguageProficiencyDocuments,
            hasBeenCompleted: () => user.english_language_proficiency_documents_approved,
            inReview: () =>
                !user.missingEnglishLanguageProficiencyDocuments &&
                !user.english_language_proficiency_documents_approved,
            onClick() {
                handleClick(this, () => loadRoute('/settings/documents'));
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDocumentsDeadline,
        },
        {
            name: 'todo_id_verify',
            localeKey: 'verify_your_identity',
            isVisible: () => !user.isAuditing && !!getActiveIdVerificationPeriod(user),
            hasBeenCompleted: () =>
                !!getActiveIdVerificationPeriod(user) && !user.unverifiedForCurrentIdVerificationPeriod,
            onClick() {
                handleClick(this, () => userIdVerificationViewModel.launchVerificationModal());
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDeadline,
        },
        {
            name: 'todo_tila_disclosure',
            localeKey: 'sign_tila_disclosure',
            isVisible: () =>
                getRequiresTilaDisclosure(user) &&
                (getHasSignedTilaDisclosure(user) || getHasTilaDisclosureSigningLink(user)),
            hasBeenCompleted: () => getHasSignedTilaDisclosure(user),
            onClick() {
                handleClick(this, () => loadUrl(getTilaDisclosureSigningLink(user), '_blank'));
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDeadline,
        },
        {
            name: 'todo_address',
            localeKey: 'provide_mailing_address',
            isVisible: () => !!user.relevantCohort?.requiresMailingAddress,
            hasBeenCompleted: () => !user.missingAddress,
            onClick() {
                handleClick(this, () => loadRoute('/settings/documents'));
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDeadline,
        },
        {
            name: 'todo_complete_student_profile',
            localeKey: 'complete_student_profile',
            isVisible: () => user.relevantCohort?.supportsNetworkAccess,
            hasBeenCompleted: () => !!user.career_profile.completedAt,
            onClick() {
                handleClick(this, () => {
                    const steps = StudentProfileFormConfigs;
                    const firstIncompleteForm = getFirstIncompleteForm(user, steps);
                    const index = steps.findIndex(step => step.stepName === firstIncompleteForm?.stepName) + 1; // 1-indexed
                    loadRoute(`/settings/my-profile?page=${index}`);
                });
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.enrollmentDeadline,
        },
        {
            name: 'todo_official_transcripts',
            localeKey: 'send_us_your_transcripts',
            isVisible: () =>
                !!user.relevantCohort?.supportsDocumentUpload && user.recordsIndicateUserShouldProvideTranscripts,
            hasBeenCompleted: () => user.transcripts_verified,
            inReview: () =>
                !user.transcripts_verified &&
                user.career_profile.educationExperienceRequiringOfficialTranscript?.officialTranscriptInReview,
            onClick() {
                handleClick(this, () =>
                    loadFaqUrl('/help/am-i-required-to-provide-official-documents-transcript-diploma-etc', '_blank'),
                );
            },
            disabled: todoDisabled,
            dueBy: user.relevantCohort?.officialTranscriptsDeadline,
            subTodosConfig: {
                isVisible: () =>
                    !!user.relevantCohort?.supportsDocumentUpload && user.recordsIndicateUserShouldProvideTranscripts,
                getSubTodos: () => [user.career_profile.educationExperienceRequiringOfficialTranscript].filter(n => n),
                hasBeenCompleted: educationExperience =>
                    educationExperience.transcriptApprovedOrWaived || user.transcripts_verified,
                inReview: educationExperience =>
                    educationExperience.officialTranscriptInReview && !user.transcripts_verified,
                getSubTodoText: educationExperience => educationExperience.degreeAndOrgNameString,
            },
        },
    ];

    // Go through all the above todoItems, grouping past due items together
    // and items due on the same day together.
    const groups = sortBy(todoItems, 'dueBy').reduce((accumulator, item) => {
        const dueBy =
            item.dueBy < new Date() && item.isVisible() && !item.hasBeenCompleted()
                ? 'pastDue'
                : formattedUserFacingMonthDayYearLong(item.dueBy);
        accumulator[dueBy] ||= [];
        accumulator[dueBy].push(item);

        // Putting the items with sub-checklist items at the bottom of the group makes for a cleaner UI
        accumulator[dueBy] = sortBy(accumulator[dueBy], todoItem => (todoItem.subTodosConfig ? '1' : 0));
        return accumulator;
    }, {});

    return [todoItems, groups];
}
